import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';
import uuid from 'react-uuid';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { startRolePlaySession, StartSessionPayload } from 'redux/actions/ai-role-play';
import { wrapThunkAction } from 'redux/utils';

import { Button } from 'react-bootstrap';
import {
  doubleSpacing,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { danger, genAIGradientType4, gray5 } from 'styles/global_defaults/colors';
import AiConversationContext,
{ AIRolePlayStatus, MessageStatus, Role } from './context';
import ConversationModalHeader from './header';
import ScenarioDetails from './scenario-details';
import ChatBar from './chat-bar';
import useAiRolePlayHelper from '../hooks/use-ai-role-play-helper';
import streamAIResponse from './stream-ai-response';
import CompleteStatus from './complete-status';
import AvatarContainer from './avatar-container';
import Chats from './chats';
import AiFeedback from './ai-feedback';

const ConversationModalContent = () => {
  const { getResponsivePadding, isTabletOrSmaller } = useAiRolePlayHelper();
  const {
    aiRolePlayId,
    aiRolePlayStatus,
    setAiRolePlayStatus,
    showScenarioDetails,
    setShowScenarioDetails,
    messages,
    setMessages,
    setSessionData,
    setIsTimeout,
    abortControllerRef,
  } = useContext(AiConversationContext);

  const isBodyPartitioned = !isTabletOrSmaller
    && aiRolePlayStatus === AIRolePlayStatus.COMPLETED;

  const styles = css`
    border-top: ${quarterSpacing}px solid transparent;
    border-image-source: ${genAIGradientType4};
    border-image-slice: 1;
    .body-container {
      min-height: 0;
      flex-direction: ${isBodyPartitioned ? 'row' : 'column'};
      overflow-y: ${isBodyPartitioned ? 'hidden' : 'auto'};
    }
    .conversation-container {
      .scenario-toggle-button {
        margin-top: 25px;
      }
      height: ${isBodyPartitioned ? 'auto' : '100%'};
      width: ${isBodyPartitioned ? '50%' : '100%'};
      .conversation-body-container {
        min-height: 0;
        flex: 1;
        overflow-y: auto;
      }
      .conversation-footer-container {
        flex-shrink: 0;
        .complete-status-container {
          margin-bottom: ${isBodyPartitioned ? standardSpacing : doubleSpacing}px;
        }
      }
      .conversation-body-container, .conversation-footer-container {
        padding-left: ${getResponsivePadding(isBodyPartitioned)}px;
        padding-right: ${getResponsivePadding(isBodyPartitioned)}px;
      }
    }
    .feedback-container {
      width: ${isBodyPartitioned ? '50%' : '100%'};
      border-left: ${isBodyPartitioned ? 1 : 0}px solid ${gray5};
      overflow-y: ${isBodyPartitioned ? 'auto' : 'unset'};
    }
    .scroll-handler {
      width: ${doubleSpacing}px;
      min-height: ${doubleSpacing}px;
    }
  `;

  const currentCatalogId = useSelector((state) => state.app.currentCatalogId);
  const dispatch = useDispatch();
  const conversationBodyRef = useRef(null);

  const [isBodyScrollPresent, setIsBodyScrollPresent] = useState(false);
  const [isBodyScrollBarAtBottom, setIsBodyScrollBarAtBottom] = useState(true);
  const [openingNarrative, setOpeningNarrative] = useState('');
  const [isRolePlayStarting, setIsRolePlayStarting] = useState(false);
  const hasReceivedNarrativeRef = useRef(false);

  useEffect(() => {
    const bodyElement = conversationBodyRef.current;
    let resizeObserver: ResizeObserver | null = null;
    const checkScroll = () => {
      setIsBodyScrollPresent(bodyElement.scrollHeight > bodyElement.clientHeight);
      const isAtBottom = Math.abs(bodyElement.scrollHeight - bodyElement.scrollTop - bodyElement.clientHeight) < 1;
      setIsBodyScrollBarAtBottom(isAtBottom);
    };

    if (bodyElement) {
      bodyElement.addEventListener('scroll', checkScroll);
      // ResizeObserver to detect content/height changes and keep scroll state in sync.
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          checkScroll();
        });
        resizeObserver.observe(bodyElement);
      }
    }

    return () => {
      if (bodyElement) {
        bodyElement.removeEventListener('scroll', checkScroll);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      }
    };
  }, []);

  const lastMessageStatus = messages[messages.length - 1]?.status;
  const totalMessages = messages.length;

  // Auto-scroll to bottom when a new message is added or when the last message's status changes (e.g. from pending to sent).
  useEffect(() => {
    const bodyElement = conversationBodyRef.current;
    const timeoutId = setTimeout(() => {
      // Guard in case the element has been detached or does not support scrollTo.
      if (bodyElement && typeof bodyElement.scrollTo === 'function') {
        bodyElement.scrollTo({
          top: bodyElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    });
    return () => {
      clearTimeout(timeoutId);
    };
  }, [lastMessageStatus, totalMessages]);

  const handleToggleScenarioDetails = () => {
    setShowScenarioDetails(!showScenarioDetails);
  };

  // To pass as callback to streamAIResponse for handling incoming response chunks and updating the conversation in real-time.
  const handleChunk = useCallback((chunk) => {
    // set initial message as opening narrative
    if (!hasReceivedNarrativeRef.current) {
      setOpeningNarrative((prev) => prev + chunk.content);
      if (chunk.is_last_chunk) {
        hasReceivedNarrativeRef.current = true;
      }
    } else {
      setMessages((prevMessages) => {
        const lastMessage = prevMessages[prevMessages.length - 1];
        if (lastMessage && lastMessage.role === Role.AI && lastMessage.status === MessageStatus.PENDING) {
        // Update the pending AI message with new content.
          const updatedMessage = {
            ...lastMessage,
            content: lastMessage.content + chunk.content,
            status: chunk.is_last_chunk ? MessageStatus.SENT : MessageStatus.PENDING,
          };
          return [...prevMessages.slice(0, -1), updatedMessage];
        }
        // Mark the learner message as SENT now that AI is responding, then add AI message
        const updatedMessages = prevMessages.map((msg) => {
          if (msg.role === Role.LEARNER && msg.status === MessageStatus.PENDING) {
            return { ...msg, status: MessageStatus.SENT };
          }
          return msg;
        });
        // Add a new AI message.
        return [...updatedMessages,
          { id: uuid(),
            content: chunk.content,
            role: Role.AI,
            status: chunk.is_last_chunk ? MessageStatus.SENT : MessageStatus.PENDING,
          }];
      });
    }

    if (chunk.is_last_message && chunk.is_last_chunk) {
      setAiRolePlayStatus(AIRolePlayStatus.COMPLETED);

      // ToDo call API for submission info
    }
  }, [setAiRolePlayStatus, setMessages]);

  const handleError = useCallback(() => {
    dispatch(addAlertMessage({
      type: AlertMessageType.WARNING,
      header: t.FORM.OOPS(),
      message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
      customColor: danger,
    }));
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      if (lastMessage) {
        const updatedMessage = {
          ...lastMessage,
          status: MessageStatus.FAILED,
        };
        return [...prevMessages.slice(0, -1), updatedMessage];
      }
      return prevMessages;
    });
  }, [dispatch, setMessages]);

  const handleStartConversation = useCallback(() => {
    setIsRolePlayStarting(true);

    // Abort any existing stream before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    wrapThunkAction(
      dispatch(startRolePlaySession({
        catalogId: currentCatalogId,
        aiRolePlayId,
      })),
    ).then((res) => {
      const sessionPayload = res.payload as StartSessionPayload;

      if (!sessionPayload?.jwtToken) {
        handleError();
        return;
      }
      setSessionData({
        jwtToken: sessionPayload.jwtToken,
        submissionId: sessionPayload.submissionId || null,
        expiresAt: sessionPayload.expiresAt || null,
      });
      setMessages([]);
      setOpeningNarrative('');
      hasReceivedNarrativeRef.current = false;
      setAiRolePlayStatus(AIRolePlayStatus.IN_PROGRESS);
      setShowScenarioDetails(false);
      setIsTimeout(false);

      streamAIResponse({
        token: sessionPayload.jwtToken,
        userMessage: null,
        onChunk: handleChunk,
        onError: handleError,
        abortSignal: abortControllerRef.current?.signal,
      }).catch((_error) => {
        // handleError was already called by onError callback, just reset state
        setAiRolePlayStatus(AIRolePlayStatus.NOT_STARTED);
        setOpeningNarrative('');
        hasReceivedNarrativeRef.current = false;
        setSessionData({ jwtToken: null, submissionId: null, expiresAt: null });
      });
    }).catch(() => {
      setAiRolePlayStatus(AIRolePlayStatus.NOT_STARTED);
      setSessionData({ jwtToken: null, submissionId: null, expiresAt: null });
      handleError();
    }).finally(() => {
      setIsRolePlayStarting(false);
    });
  }, [
    dispatch,
    currentCatalogId,
    aiRolePlayId,
    setShowScenarioDetails,
    setAiRolePlayStatus,
    setSessionData,
    handleChunk,
    handleError,
    setMessages,
    setIsTimeout,
    abortControllerRef,
  ]);

  const handleScroll = () => {
    if (conversationBodyRef.current) {
      if (!isBodyScrollBarAtBottom) {
        conversationBodyRef.current.scrollTo({
          top: conversationBodyRef.current.scrollHeight,
          behavior: 'smooth',
        });
        return;
      }
      conversationBodyRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div css={styles} className='modal-page position-relative d-flex flex-column'>
      <ConversationModalHeader
        isRolePlayStarting={isRolePlayStarting}
        startConversation={handleStartConversation}
      />
      <div className='body-container h-100 d-flex border-top'>
        <div className='conversation-container d-flex flex-column pb-1'>
          <div ref={conversationBodyRef} className='mt-5 conversation-body-container'>
            <AvatarContainer />
            {showScenarioDetails && (
              <ScenarioDetails />
            )}
            {(aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS || aiRolePlayStatus === AIRolePlayStatus.COMPLETED)
            && (
            <ClickableContainer
              className='mb-0 text-primary d-flex justify-content-center scenario-toggle-button text-small semi-bold'
              onClick={handleToggleScenarioDetails}
            >
              <NvIcon
                icon='gen-ai'
                size='xss-smallest'
                className='d-flex align-items-center'
              />
              <p className='mb-0 ml-1'>
                {showScenarioDetails
                  ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.HIDE_SCENARIO()
                  : t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SHOW_SCENARIO()}
              </p>
            </ClickableContainer>
            )}
            {aiRolePlayStatus === AIRolePlayStatus.NOT_STARTED && (
            <div className='d-flex justify-content-center mt-5'>
              <Button
                onClick={handleStartConversation}
                disabled={isRolePlayStarting}
              >
                {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.BEGIN()}
              </Button>
            </div>
            )}
            {(aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS || aiRolePlayStatus === AIRolePlayStatus.COMPLETED)
            && (
              <Chats openingNarrative={openingNarrative} />
            )}
          </div>
          <div className='w-100 conversation-footer-container'>
            {isBodyScrollPresent && (
            <ClickableContainer
              className='d-flex bg-gray-2 scroll-handler rounded-circle mt-auto ml-auto'
              onClick={handleScroll}
            >
              <NvIcon
                className='text-white d-flex align-items-center m-auto'
                icon={isBodyScrollBarAtBottom ? 'sorting-up-active' : 'sorting-down-active'}
                size='xss-smallest'
              />
            </ClickableContainer>
            )}
            {aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS && (
              <ChatBar
                handleChunk={handleChunk}
                handleError={handleError}
                abortSignal={abortControllerRef.current?.signal}
              />
            )}
            {aiRolePlayStatus === AIRolePlayStatus.COMPLETED && (
              <div className='complete-status-container mt-auto pt-4'>
                <CompleteStatus />
              </div>
            )}
          </div>
        </div>
        {
          aiRolePlayStatus === AIRolePlayStatus.COMPLETED && (
            <div className='feedback-container'>
              <AiFeedback />
            </div>
          )
        }
      </div>
    </div>
  );
};

export default ConversationModalContent;
